/**
 * Keep-screen-awake — hold a screen wake lock so the phone doesn't dim and sleep
 * mid-pilotage, when the skipper is watching the chart but not touching it.
 *
 * Uses the WebView's Screen Wake Lock API (navigator.wakeLock), which the
 * Capacitor Android WebView supports. No native plugin to sync, and it degrades
 * gracefully: on a browser/WebView without the API it's a quiet no-op rather
 * than an error. The OS auto-releases a wake lock whenever the page is hidden
 * (app backgrounded / screen turned off by the user), so we re-acquire it on the
 * way back to the foreground while the toggle is still on.
 *
 * Default OFF: holding the screen on burns battery, so it's the skipper's call.
 */

// Minimal typing: the DOM lib in this project doesn't ship WakeLock types.
interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
}
interface WakeLockLike {
  request: (type: 'screen') => Promise<WakeLockSentinelLike>;
}

export type WakeAction = 'acquire' | 'release' | 'noop';

/**
 * The one decision, pulled out pure so it's testable: given what the user wants
 * and whether we currently hold a lock, do we grab one, drop it, or leave it be.
 */
export function wakeAction(desired: boolean, hasSentinel: boolean): WakeAction {
  if (desired && !hasSentinel) return 'acquire';
  if (!desired && hasSentinel) return 'release';
  return 'noop';
}

/** True when the running environment offers the Screen Wake Lock API. */
export function wakeLockSupported(nav: unknown = typeof navigator !== 'undefined' ? navigator : undefined): boolean {
  return !!nav && typeof nav === 'object' && 'wakeLock' in nav;
}

function wakeLockApi(): WakeLockLike | null {
  if (!wakeLockSupported()) return null;
  return (navigator as unknown as { wakeLock: WakeLockLike }).wakeLock;
}

let sentinel: WakeLockSentinelLike | null = null;
let desired = false;

async function acquire(): Promise<void> {
  if (wakeAction(desired, sentinel != null) !== 'acquire') return;
  const api = wakeLockApi();
  if (!api) return;
  try {
    const s = await api.request('screen');
    // If the user turned it off while we were awaiting, drop it straight back.
    if (!desired) {
      try {
        await s.release();
      } catch {
        /* ignore */
      }
      return;
    }
    sentinel = s;
    // The OS releases the lock when the page hides; reflect that so we know to
    // re-acquire on the way back.
    s.addEventListener('release', () => {
      sentinel = null;
    });
  } catch {
    sentinel = null;
  }
}

async function release(): Promise<void> {
  const s = sentinel;
  sentinel = null;
  if (s) {
    try {
      await s.release();
    } catch {
      /* ignore */
    }
  }
}

/** Turn the wake lock on or off. Safe to call repeatedly / on unsupported hosts. */
export function setKeepAwake(on: boolean): void {
  desired = on;
  if (on) void acquire();
  else void release();
}

// A wake lock is auto-released whenever the page is hidden, so re-take it when we
// come back to the foreground and the toggle is still on. Registered once.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && desired) void acquire();
  });
}
